import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BookingsSettings,
  BookingsSettingsCreatedEnvelope,
  BookingsSettingsDeletedEnvelope,
  BookingsSettingsUpdatedEnvelope,
  GetBookingsSettingsApplicationErrors,
  getBookingsSettings as universalGetBookingsSettings,
  updateBookingsSettings as universalUpdateBookingsSettings,
} from './bookings-v2-bookings-settings-bookings-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function getBookingsSettings(
  httpClient: HttpClient
): GetBookingsSettingsSignature {
  return () =>
    universalGetBookingsSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface GetBookingsSettingsSignature {
  /**
   * Retrieves the site's bookings settings.
   *
   * If no settings have been customized for the site yet, the default settings are returned.
   * @returns Retrieved bookings settings.
   */
  (): Promise<
    NonNullablePaths<BookingsSettings, `displayTimeZone.basedOn`, 3> & {
      __applicationErrorsType?: GetBookingsSettingsApplicationErrors;
    }
  >;
}

export function updateBookingsSettings(
  httpClient: HttpClient
): UpdateBookingsSettingsSignature {
  return (
    bookingsSettings: NonNullablePaths<BookingsSettings, `revision`, 2>
  ) =>
    universalUpdateBookingsSettings(
      bookingsSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBookingsSettingsSignature {
  /**
   * Updates the site's bookings settings.
   *
   * Each time the bookings settings are updated, `revision` increments by 1. The current `revision` must be passed when updating the bookings settings. This ensures you're working with the latest bookings settings and prevents unintended overwrites.
   *
   * This is a partial update: only the fields listed in `fieldMask.paths` are changed. Fields not included in the field mask keep their current values.
   *
   * If no settings have been customized for the site yet, the default settings are created and then updated with the values you provide.
   * @param - Bookings settings to update.
   * @returns Updated bookings settings.
   */
  (
    bookingsSettings: NonNullablePaths<BookingsSettings, `revision`, 2>
  ): Promise<NonNullablePaths<BookingsSettings, `displayTimeZone.basedOn`, 3>>;
}

export const onBookingsSettingsCreated = EventDefinition(
  'wix.bookings.v2.bookings_settings_created',
  true,
  (event: BookingsSettingsCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.siteProperties.address.coordinates.latitude' },
            { path: 'entity.siteProperties.address.coordinates.longitude' },
          ],
        },
      ])
    )
)<BookingsSettingsCreatedEnvelope>();
export const onBookingsSettingsDeleted = EventDefinition(
  'wix.bookings.v2.bookings_settings_deleted',
  true,
  (event: BookingsSettingsDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.siteProperties.address.coordinates.latitude' },
            { path: 'entity.siteProperties.address.coordinates.longitude' },
          ],
        },
      ])
    )
)<BookingsSettingsDeletedEnvelope>();
export const onBookingsSettingsUpdated = EventDefinition(
  'wix.bookings.v2.bookings_settings_updated',
  true,
  (event: BookingsSettingsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.siteProperties.address.coordinates.latitude' },
            { path: 'entity.siteProperties.address.coordinates.longitude' },
            {
              path: 'modifiedFields.siteProperties.address.coordinates.latitude',
            },
            {
              path: 'modifiedFields.siteProperties.address.coordinates.longitude',
            },
          ],
        },
      ])
    )
)<BookingsSettingsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  Asset,
  BaseEventMetadata,
  BasedOn,
  BookingCreationSettings,
  BookingsSettings,
  BookingsSettingsCreatedEnvelope,
  BookingsSettingsDeletedEnvelope,
  BookingsSettingsUpdatedEnvelope,
  BusinessNotification,
  CartCheckout,
  DeleteContext,
  DeleteStatus,
  DisplayTimeZone,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  Feature,
  Features,
  GeoCoordinates,
  GetBookingsSettingsRequest,
  GetBookingsSettingsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Locale,
  LocationSelection,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  MultiServiceAppointments,
  Namespace,
  NamespaceChanged,
  Notifications,
  OdeditorAssigned,
  OdeditorUnassigned,
  OwnerFlow,
  PicassoAssigned,
  PicassoUnassigned,
  PlacementType,
  Reason,
  RequestedFields,
  RequiredField,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SiteProperties,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  StaffSelection,
  StaffSelectionTiming,
  State,
  Strategy,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Timing,
  UpdateBookingsSettingsRequest,
  UpdateBookingsSettingsResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './bookings-v2-bookings-settings-bookings-settings.universal.js';
